
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Plus } from "lucide-react";
import { BookForm } from "./BookForm";
import { CustomPagination } from "@/components/CustomPagination";
import { BookTable } from "./BookTable";
import { BookSearch } from "./BookSearch";
import { useBooks } from "./hooks/useBooks";
import { useBookActions } from "./hooks/useBookActions";
import { type Book } from "./types";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export function BookManagement() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingBook, setEditingBook] = useState<Book | null>(null);
  
  const { 
    books, 
    loading, 
    error, 
    currentPage, 
    totalPages, 
    setCurrentPage,
    handleSearch,
    refreshBooks
  } = useBooks();
  
  const { deleteBook } = useBookActions(refreshBooks);

  const handleEditBook = (book: Book) => {
    setEditingBook(book);
    setIsFormOpen(true);
  };

  const handleAddBook = () => {
    setEditingBook(null);
    setIsFormOpen(true);
  };

  const handleFormClose = (refreshData: boolean = false) => {
    setIsFormOpen(false);
    setEditingBook(null);
    if (refreshData) {
      refreshBooks();
    }
  };

  if (loading && books.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error && books.length === 0) {
    return (
      <div className="p-4 rounded-md bg-red-900/20 border border-red-800/30 text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Library Management</h2>
        <Button onClick={handleAddBook} className="flex items-center">
          <Plus className="mr-2 h-4 w-4" /> Add New Book
        </Button>
      </div>

      <BookSearch onSearch={handleSearch} />

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Author</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Reading Time</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <BookTable 
              books={books} 
              onEdit={handleEditBook} 
              onDelete={deleteBook} 
            />
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex justify-center">
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {isFormOpen && (
        <BookForm book={editingBook} onClose={handleFormClose} />
      )}
    </div>
  );
}
